import React, { useState } from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  useColorMode,
  SimpleGrid,
  Icon,
  Flex,
  List,
  ListItem,
  ListIcon,
  Divider,
  Badge,
  Button,
} from '@chakra-ui/react';
import { NavbarWithCallToAction } from '../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction';
import { FooterWithFourColumns } from '../../components/chakra/FooterWithFourColumns/FooterWithFourColumns';
import { getColor } from '../../brandConfig';
import { FaGamepad, FaCheckCircle, FaArrowRight, FaCogs, FaLayerGroup, FaCode } from 'react-icons/fa';
import { GiPokerHand, GiCardRandom, GiCardPick } from 'react-icons/gi';
import ListBuildingModal from '../../components/ListBuildingModal';

export const PokerVirtualMachine = () => {
  const { colorMode } = useColorMode();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const features = [
    {
      icon: GiCardRandom,
      title: 'Universal Poker Functions',
      description: 'A complete set of standardized functions for all poker game mechanics—betting, folding, raising, shuffling, and dealing.',
      color: '#3b82f6'
    },
    {
      icon: FaLayerGroup,
      title: 'Game Variants',
      description: 'Build different poker variants (Texas Hold\'em, Omaha, etc.) using the same underlying primitive functions.',
      color: '#64ffda'
    },
    {
      icon: FaCogs,
      title: 'Configurable Rules',
      description: 'Customize game rules, betting structures, and tournament formats through simple configuration.',
      color: '#3b82f6'
    },
    {
      icon: FaCode,
      title: 'Developer-Friendly',
      description: 'Clean APIs and documentation make it easy to build poker applications without deep cryptography knowledge.',
      color: '#64ffda'
    }
  ];

  const gameTypes = [
    'Texas Hold\'em (Cash Games & Tournaments)',
    'Omaha (PLO, Omaha Hi-Lo)',
    'Seven Card Stud',
    'Five Card Draw',
    'Short Deck / 6+ Hold\'em',
    'Custom Poker Variants'
  ];

  return (
    <>
      <NavbarWithCallToAction />
      <Box minH="100vh" bg={getColor('background.main', colorMode)} py={20}>
        <Container maxW="900px">
          <VStack spacing={8} align="start">
            {/* Header */}
            <VStack spacing={4} align="start" w="full">
              <Badge
                px={4}
                py={2}
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                color="white"
                borderRadius="full"
                fontSize="sm"
                fontWeight="bold"
              >
                LEARN
              </Badge>
              <Heading
                as="h1"
                size="2xl"
                color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}
              >
                Poker Virtual Machine (PVM)
              </Heading>
              <Text fontSize="xl" color={getColor('text.secondary', colorMode)} lineHeight="tall">
                The Poker Virtual Machine is a specialized execution environment built on top of the Block52 blockchain.
                It provides a complete set of universal functions that enable developers to build fair, transparent poker
                games online without requiring deep cryptography expertise.
              </Text>
            </VStack>

            <Divider />

            {/* What is a Virtual Machine? */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                What is a Virtual Machine?
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                In blockchain technology, a virtual machine is a software layer that runs on top of the base blockchain,
                providing specialized functionality for specific use cases. Just like Ethereum has the EVM (Ethereum Virtual Machine)
                for smart contracts, Block52 has the PVM (Poker Virtual Machine) for card games.
              </Text>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall">
                The PVM handles all the complex cryptographic operations needed for fair online poker—shuffling, dealing,
                hand evaluation, pot calculation, and more—so developers can focus on building great poker experiences
                instead of reinventing the wheel.
              </Text>
            </Box>

            {/* How It Works */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                How the PVM Works
              </Heading>
              <VStack spacing={4} align="start">
                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
                  w="full"
                >
                  <Flex align="start">
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="full"
                      bg="rgba(59, 130, 246, 0.2)"
                      align="center"
                      justify="center"
                      mr={4}
                      flexShrink={0}
                    >
                      <Text fontWeight="bold" color="#3b82f6">1</Text>
                    </Flex>
                    <Box>
                      <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        Primitive Functions
                      </Heading>
                      <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                        The PVM provides low-level "primitive" functions for all poker operations—shuffle(), deal(),
                        bet(), fold(), raise(), reveal(). These are the building blocks for any poker game.
                      </Text>
                    </Box>
                  </Flex>
                </Box>

                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.200'}
                  w="full"
                >
                  <Flex align="start">
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="full"
                      bg="rgba(100, 255, 218, 0.2)"
                      align="center"
                      justify="center"
                      mr={4}
                      flexShrink={0}
                    >
                      <Text fontWeight="bold" color="#64ffda">2</Text>
                    </Flex>
                    <Box>
                      <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        Configuration Layer
                      </Heading>
                      <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                        Developers configure their games by combining these primitives with game rules (Texas Hold'em,
                        Omaha, etc.), betting structures (No Limit, Pot Limit, Fixed Limit), and tournament formats.
                      </Text>
                    </Box>
                  </Flex>
                </Box>

                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
                  w="full"
                >
                  <Flex align="start">
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="full"
                      bg="rgba(59, 130, 246, 0.2)"
                      align="center"
                      justify="center"
                      mr={4}
                      flexShrink={0}
                    >
                      <Text fontWeight="bold" color="#3b82f6">3</Text>
                    </Flex>
                    <Box>
                      <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        Cryptographic Execution
                      </Heading>
                      <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                        The PVM executes all game logic using cryptography to ensure fairness. Shuffling uses verifiable
                        randomness, dealing uses commit-reveal schemes, and all actions are recorded on-chain.
                      </Text>
                    </Box>
                  </Flex>
                </Box>

                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.200'}
                  w="full"
                >
                  <Flex align="start">
                    <Flex
                      w="32px"
                      h="32px"
                      borderRadius="full"
                      bg="rgba(100, 255, 218, 0.2)"
                      align="center"
                      justify="center"
                      mr={4}
                      flexShrink={0}
                    >
                      <Text fontWeight="bold" color="#64ffda">4</Text>
                    </Flex>
                    <Box>
                      <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        Verification & Settlement
                      </Heading>
                      <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                        Players can verify every action, the blockchain validates all game state transitions, and winnings
                        are automatically distributed according to the rules—no disputes, no delays.
                      </Text>
                    </Box>
                  </Flex>
                </Box>
              </VStack>
            </Box>

            {/* Key Features */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={6} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Key Features
              </Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                {features.map((feature, index) => (
                  <Box
                    key={index}
                    p={6}
                    borderRadius="lg"
                    bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                    border="1px solid"
                    borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
                    transition="all 0.3s"
                    _hover={{
                      transform: 'translateY(-4px)',
                      borderColor: feature.color,
                      boxShadow: `0 8px 16px ${feature.color}30`,
                    }}
                  >
                    <Flex align="start" mb={3}>
                      <Flex
                        w="48px"
                        h="48px"
                        borderRadius="lg"
                        bg={`${feature.color}20`}
                        align="center"
                        justify="center"
                        mr={4}
                        flexShrink={0}
                      >
                        <Icon as={feature.icon} color={feature.color} boxSize={6} />
                      </Flex>
                      <Heading size="sm" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                        {feature.title}
                      </Heading>
                    </Flex>
                    <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                      {feature.description}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </Box>

            {/* Supported Game Types */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Supported Game Types
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} mb={4}>
                The PVM supports multiple poker variants, all built from the same universal primitive functions:
              </Text>
              <List spacing={3}>
                {gameTypes.map((game, index) => (
                  <ListItem key={index}>
                    <Flex align="center">
                      <ListIcon as={FaCheckCircle} color="#64ffda" fontSize="xl" />
                      <Text color={getColor('text.secondary', colorMode)}>{game}</Text>
                    </Flex>
                  </ListItem>
                ))}
              </List>
            </Box>

            {/* Fair Play Guarantee */}
            <Box
              w="full"
              p={6}
              bgGradient={colorMode === 'dark'
                ? 'linear(to-r, rgba(59, 130, 246, 0.1), rgba(100, 255, 218, 0.1))'
                : 'linear(to-r, blue.50, teal.50)'}
              borderRadius="xl"
              border="2px solid"
              borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : '#3b82f6'}
            >
              <Flex align="center" mb={4}>
                <Icon as={GiPokerHand} color="#3b82f6" boxSize={8} mr={3} />
                <Heading as="h2" size="lg" color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                  Fair Play Guarantee
                </Heading>
              </Flex>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={3}>
                Every poker game running on the PVM is provably fair. Players don't have to trust the house, the dealer,
                or the platform operator. The cryptographic guarantees built into the PVM ensure that:
              </Text>
              <VStack align="start" spacing={2}>
                <Flex align="center">
                  <Icon as={FaCheckCircle} color="#64ffda" mr={2} />
                  <Text color={getColor('text.secondary', colorMode)}>
                    No one can see cards before they're revealed
                  </Text>
                </Flex>
                <Flex align="center">
                  <Icon as={FaCheckCircle} color="#64ffda" mr={2} />
                  <Text color={getColor('text.secondary', colorMode)}>
                    Shuffles are truly random and cannot be manipulated
                  </Text>
                </Flex>
                <Flex align="center">
                  <Icon as={FaCheckCircle} color="#64ffda" mr={2} />
                  <Text color={getColor('text.secondary', colorMode)}>
                    All game actions are recorded and verifiable
                  </Text>
                </Flex>
                <Flex align="center">
                  <Icon as={FaCheckCircle} color="#64ffda" mr={2} />
                  <Text color={getColor('text.secondary', colorMode)}>
                    Winnings are automatically and accurately distributed
                  </Text>
                </Flex>
              </VStack>
            </Box>

            {/* Developer Benefits */}
            <Box w="full">
              <Heading as="h2" size="lg" mb={4} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                Why Build on the PVM?
              </Heading>
              <Text color={getColor('text.secondary', colorMode)} lineHeight="tall" mb={4}>
                Building a fair online poker game from scratch requires expertise in cryptography, distributed systems,
                and game theory. The PVM abstracts away this complexity, allowing developers to focus on creating great
                user experiences.
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
                >
                  <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    No Cryptography Required
                  </Heading>
                  <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                    All the complex cryptographic operations are handled by the PVM. Just call simple functions.
                  </Text>
                </Box>
                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.200'}
                >
                  <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Battle-Tested Security
                  </Heading>
                  <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                    Benefit from security audits and continuous improvement of the core PVM functions.
                  </Text>
                </Box>
                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(59, 130, 246, 0.3)' : 'gray.200'}
                >
                  <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Faster Development
                  </Heading>
                  <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                    Launch your poker platform in weeks instead of years by leveraging pre-built components.
                  </Text>
                </Box>
                <Box
                  p={5}
                  bg={colorMode === 'dark' ? 'rgba(31, 41, 55, 0.3)' : 'white'}
                  borderRadius="lg"
                  border="1px solid"
                  borderColor={colorMode === 'dark' ? 'rgba(100, 255, 218, 0.3)' : 'gray.200'}
                >
                  <Heading size="sm" mb={2} color={colorMode === 'light' ? getColor('text.primary', colorMode) : 'white'}>
                    Player Trust
                  </Heading>
                  <Text color={getColor('text.secondary', colorMode)} fontSize="sm">
                    Players know the game is provably fair, increasing confidence and retention.
                  </Text>
                </Box>
              </SimpleGrid>
            </Box>

            {/* CTA */}
            <Box w="full" textAlign="center" py={6}>
              <Button
                size="lg"
                px={8}
                py={6}
                bgGradient="linear(to-r, #3b82f6, #64ffda)"
                color="white"
                _hover={{
                  bgGradient: 'linear(to-r, #64ffda, #3b82f6)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 20px rgba(59, 130, 246, 0.4)',
                }}
                rightIcon={<FaArrowRight />}
                borderRadius="full"
                fontWeight="bold"
                onClick={() => setIsModalOpen(true)}
              >
                Start Now
              </Button>
            </Box>
          </VStack>
        </Container>
      </Box>
      <FooterWithFourColumns />
      <ListBuildingModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
};

export default PokerVirtualMachine;
